import React from 'react';
import FormField from '../shared/FormField';
import CountrySelect, { getCountryName } from '../shared/CountrySelect';
import ProvinceCitySelect from '../shared/ProvinceCitySelect';
import formDataStorage from '../../utils/formDataStorage';
import { Plus, Trash2, FileText, Calendar, AlertTriangle, ChevronDown } from 'lucide-react';

// 护照签证信息表单
const PassportVisaForm = ({ data, onChange, fieldErrors = {} }) => {

  // 从formDataStorage读取数据并确保数据同步
  React.useEffect(() => {
    console.log('PassportVisaForm useEffect - 初始化数据检查');
    const savedData = formDataStorage.loadFormData('passport_visa');
    console.log('PassportVisaForm useEffect - 已保存数据:', savedData);
    console.log('PassportVisaForm useEffect - 当前props数据:', data);
    
    // 如果有保存的数据但当前data为空或部分字段缺失，则从数据库加载
    if (savedData && (!data || Object.keys(data).length === 0)) {
      console.log('PassportVisaForm useEffect - 从数据库加载数据');
      Object.keys(savedData).forEach(key => {
        if (savedData[key] !== null && savedData[key] !== undefined) {
          onChange(key, savedData[key]);
        }
      });
    }
  }, [data, onChange]);
  
  // 护照类型选项（与官方DS-160表单完全一致）
  const PASSPORT_TYPES = [
    { value: 'R', label: 'R - REGULAR (普通护照)' },
    { value: 'O', label: 'O - OFFICIAL (公务护照)' },
    { value: 'D', label: 'D - DIPLOMATIC (外交护照)' },
    { value: 'L', label: 'L - LAISSEZ-PASSER (通行证)' },
    { value: 'T', label: 'T - OTHER (其他类型)' }
  ];

  // 月份映射
  const MONTHS = [
    { value: '01', label: '01月' },
    { value: '02', label: '02月' },
    { value: '03', label: '03月' },
    { value: '04', label: '04月' },
    { value: '05', label: '05月' },
    { value: '06', label: '06月' },
    { value: '07', label: '07月' },
    { value: '08', label: '08月' },
    { value: '09', label: '09月' },
    { value: '10', label: '10月' },
    { value: '11', label: '11月' },
    { value: '12', label: '12月' }
  ];

  // 获取指定年月的天数
  const getDaysInMonth = (year, month) => {
    if (!year || !month) return 31;
    return new Date(year, month, 0).getDate();
  };

  // 生成日期选项
  const generateDayOptions = (year, month) => {
    const maxDays = getDaysInMonth(year, month);
    return Array.from({ length: maxDays }, (_, i) => {
      const day = String(i + 1).padStart(2, '0');
      return { value: day, label: `${day}日` };
    });
  };

  // 验证护照签发日期
  const validatePassportIssuedDate = () => {
    const { ppt_issued_year, ppt_issued_month, ppt_issued_day } = data;
    
    // 如果日期不完整，不进行验证
    if (!ppt_issued_year || !ppt_issued_month || !ppt_issued_day) {
      return null;
    }

    // 构建护照签发日期
    const issuedDate = new Date(ppt_issued_year, parseInt(ppt_issued_month) - 1, parseInt(ppt_issued_day));
    
    // 获取当前日期（设置为当天结束时间）
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    
    // 检查是否晚于当前日期
    if (issuedDate > today) {
      return '护照签发日期不能晚于当前日期';
    }
    
    // 从personal_1表单获取生日信息
    const personal1Data = formDataStorage.loadFormData('personal_1');
    if (personal1Data && personal1Data.birth_year && personal1Data.birth_month && personal1Data.birth_day) {
      // 处理月份格式（确保是两位数字）
      const birthMonth = personal1Data.birth_month.toString().padStart(2, '0');
      const birthDate = new Date(personal1Data.birth_year, parseInt(birthMonth) - 1, parseInt(personal1Data.birth_day));
      
      // 检查护照签发日期是否早于生日
      if (issuedDate < birthDate) {
        return '护照签发日期不能早于您的出生日期';
      }
    }
    
    return null; // 验证通过
  };

  // 更新丢失护照信息
  const updateLostPassport = (index, field, value) => {
    const currentLostPpt = data.lost_ppt || [];
    const newLostPpt = [...currentLostPpt];
    if (!newLostPpt[index]) {
      newLostPpt[index] = { lost_ppt_number: '', lost_ppt_country: '', explain: '' };
    }
    newLostPpt[index] = { ...newLostPpt[index], [field]: value };
    onChange('lost_ppt', newLostPpt);
  };

  // 添加丢失护照
  const addLostPassport = () => {
    const currentLostPpt = data.lost_ppt || [];
    const newLostPpt = [...currentLostPpt, { lost_ppt_number: '', lost_ppt_country: '', explain: '' }];
    onChange('lost_ppt', newLostPpt);
  };

  // 删除丢失护照
  const removeLostPassport = (index) => {
    const currentLostPpt = data.lost_ppt || [];
    const newLostPpt = currentLostPpt.filter((_, i) => i !== index);
    onChange('lost_ppt', newLostPpt);
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-teal-50 to-green-50 p-6 rounded-lg">
        <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <span className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">📘</span>
          护照签证信息
        </h3>

        {/* 护照基本信息 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
            <FileText className="w-5 h-5 mr-2" />
            护照基本信息
          </h4>
          
          <div className="space-y-4">
            {/* 护照类型 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                护照类型 <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={data.ppt_type || ''}
                  onChange={(e) => {
                    const newType = e.target.value;
                    onChange('ppt_type', newType);
                    // 如果不是T类型，清空说明字段
                    if (newType !== 'T') {
                      onChange('ppt_explain', '');
                    }
                  }}
                  className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-gradient-to-r from-white to-teal-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer"
                >
                  <option value="">请选择护照类型...</option>
                  {PASSPORT_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </div>
              </div>
            </div>

            {/* 护照类型说明 - 只有选择T(OTHER)类型时才显示 */}
            {data.ppt_type === 'T' && (
              <FormField
                label="护照类型说明（其他类型详情）"
                value={data.ppt_explain || ''}
                onChange={(e) => onChange('ppt_explain', e.target.value)}
                placeholder="请详细说明您的护照类型..."
                required
              />
            )}

            {/* 护照号码和护照本号码 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                label="护照号码"
                value={data.ppt_number || ''}
                onChange={(e) => onChange('ppt_number', e.target.value)}
                placeholder="TEST114514"
                required
              />
              <FormField
                label="护照本号码"
                value={data.ppt_book_number || ''}
                onChange={(e) => onChange('ppt_book_number', e.target.value)}
                placeholder="TEST1919810"
              />
            </div>
          </div>
        </div>

        {/* 护照签发信息 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
            <Calendar className="w-5 h-5 mr-2" />
            护照签发信息
          </h4>
          
          <div className="space-y-4">
            {/* 签发国家 */}
            <CountrySelect
              label="颁发护照/旅行证件的国家/机构"
              value={data.ppt_issued_country || ''}
              onChange={(value) => onChange('ppt_issued_country', value)}
              required
            />

            {/* 签发城市和州/省 */}
            {['CHIN', 'MAC', 'HNK', 'TWAN'].includes(data.ppt_issued_country) ? (
              <ProvinceCitySelect
                provinceLabel="签发省份"
                cityLabel="签发城市"
                provinceValue={data.ppt_issued_state || ''}
                cityValue={data.ppt_issued_city || ''}
                onProvinceChange={(province) => {
                   onChange('ppt_issued_state', province);
                   if (data.ppt_issued_city) {
                     onChange('ppt_issued_city', '');
                   }
                 }}
                onCityChange={(city) => onChange('ppt_issued_city', city)}
                required
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="签发城市"
                  value={data.ppt_issued_city || ''}
                  onChange={(e) => onChange('ppt_issued_city', e.target.value)}
                  placeholder="BEIJING"
                  required
                />
                <FormField
                  label="签发州/省"
                  value={data.ppt_issued_state || ''}
                  onChange={(e) => onChange('ppt_issued_state', e.target.value)}
                  placeholder="BEIJING"
                  required
                />
              </div>
            )}

            {/* 护照签发日期 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                护照签发日期 <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-2">年份</label>
                  <input
                    id="ppt_issued_year"
                    type="number"
                    min="1900"
                    max="2030"
                    placeholder="2019"
                    value={data.ppt_issued_year || ''}
                    onChange={(e) => onChange('ppt_issued_year', e.target.value)}
                    className="w-full h-12 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-center bg-gradient-to-r from-white to-teal-50 shadow-sm hover:shadow-md transition-all duration-200 font-medium"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-2">月份</label>
                  <div className="relative">
                    <select
                      id="ppt_issued_month"
                      value={data.ppt_issued_month || ''}
                      onChange={(e) => onChange('ppt_issued_month', e.target.value)}
                      className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-gradient-to-r from-white to-teal-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium"
                    >
                      <option value="">选择月份</option>
                      {MONTHS.map(month => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-2">日期</label>
                  <div className="relative">
                    <select
                      id="ppt_issued_day"
                      value={data.ppt_issued_day || ''}
                      onChange={(e) => onChange('ppt_issued_day', e.target.value)}
                      disabled={!data.ppt_issued_year || !data.ppt_issued_month}
                      className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-gradient-to-r from-white to-teal-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      <option value="">选择日期</option>
                      {generateDayOptions(data.ppt_issued_year, data.ppt_issued_month).map(day => (
                        <option key={day.value} value={day.value}>
                          {day.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* 护照签发日期验证错误提示 */}
            {(() => {
              const error = validatePassportIssuedDate();
              if (error) {
                return (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
                    <AlertTriangle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-red-700 text-sm font-medium">{error}</span>
                  </div>
                );
              }
              return null;
            })()}
          </div>
        </div>

        {/* 护照到期信息 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
              <Calendar className="w-5 h-5 mr-2" />
              护照到期信息
            </h4>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                您的护照是否有到期日期？
              </label>
              <div className="flex gap-6">
                <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-teal-400 hover:bg-teal-50 transition-all duration-200">
                  <input
                    type="radio"
                    name="has_ppt_expiration"
                    value="false"
                    checked={data.has_ppt_expiration === false}
                    onChange={(e) => {
                      onChange('has_ppt_expiration', false);
                      onChange('ppt_expired_year', '');
                      onChange('ppt_expired_month', '');
                      onChange('ppt_expired_day', '');
                    }}
                    className="mr-3 w-5 h-5 text-teal-600 border-gray-300 focus:ring-teal-500 focus:ring-2"
                  />
                  <span className="text-base font-medium text-gray-700">❌ 否 - 护照无到期日期</span>
                </label>
                
                <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-teal-400 hover:bg-teal-50 transition-all duration-200">
                  <input
                    type="radio"
                    name="has_ppt_expiration"
                    value="true"
                    checked={data.has_ppt_expiration === true}
                    onChange={(e) => onChange('has_ppt_expiration', true)}
                    className="mr-3 w-5 h-5 text-teal-600 border-gray-300 focus:ring-teal-500 focus:ring-2"
                  />
                  <span className="text-base font-medium text-gray-700">✅ 是 - 护照有到期日期</span>
                </label>
              </div>
            </div>
          </div>

          {data.has_ppt_expiration && (
            <div className="p-4 bg-gradient-to-r from-teal-50 to-green-50 rounded-lg border-l-4 border-teal-400">
              <h5 className="text-md font-semibold text-gray-700 mb-4">护照到期日期</h5>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-2">年份</label>
                  <input
                    id="ppt_expired_year"
                    type="number"
                    min="2020"
                    max="2050"
                    placeholder="2026"
                    value={data.ppt_expired_year || ''}
                    onChange={(e) => onChange('ppt_expired_year', e.target.value)}
                    className="w-full h-12 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-center bg-gradient-to-r from-white to-teal-50 shadow-sm hover:shadow-md transition-all duration-200 font-medium"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-2">月份</label>
                  <div className="relative">
                    <select
                      id="ppt_expired_month"
                      value={data.ppt_expired_month || ''}
                      onChange={(e) => onChange('ppt_expired_month', e.target.value)}
                      className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-gradient-to-r from-white to-teal-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium"
                    >
                      <option value="">选择月份</option>
                      {MONTHS.map(month => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-2">日期</label>
                  <div className="relative">
                    <select
                      id="ppt_expired_day"
                      value={data.ppt_expired_day || ''}
                      onChange={(e) => onChange('ppt_expired_day', e.target.value)}
                      disabled={!data.ppt_expired_year || !data.ppt_expired_month}
                      className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-gradient-to-r from-white to-teal-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      <option value="">选择日期</option>
                      {generateDayOptions(data.ppt_expired_year, data.ppt_expired_month).map(day => (
                        <option key={day.value} value={day.value}>
                          {day.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 丢失护照信息 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
              <AlertTriangle className="w-5 h-5 mr-2" />
              丢失护照信息
            </h4>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                您是否有过丢失或被盗的护照？
              </label>
              <div className="flex gap-6">
                <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition-all duration-200">
                  <input
                    type="radio"
                    name="ppt_lost"
                    value="false"
                    checked={data.ppt_lost === false}
                    onChange={(e) => {
                      onChange('ppt_lost', false);
                      onChange('lost_ppt', []);
                    }}
                    className="mr-3 w-5 h-5 text-red-600 border-gray-300 focus:ring-red-500 focus:ring-2"
                  />
                  <span className="text-base font-medium text-gray-700">❌ 否 - 从未丢失过护照</span>
                </label>
                
                <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition-all duration-200">
                  <input
                    type="radio"
                    name="ppt_lost"
                    value="true"
                    checked={data.ppt_lost === true}
                    onChange={(e) => {
                      onChange('ppt_lost', true);
                      if (!data.lost_ppt || data.lost_ppt.length === 0) {
                        addLostPassport();
                      }
                    }}
                    className="mr-3 w-5 h-5 text-red-600 border-gray-300 focus:ring-red-500 focus:ring-2"
                  />
                  <span className="text-base font-medium text-gray-700">⚠️ 是 - 我有丢失过护照</span>
                </label>
              </div>
            </div>
          </div>

          {data.ppt_lost && (
            <div className="p-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-lg border-l-4 border-red-400">
              <div className="flex items-center justify-between mb-4">
                <h5 className="text-md font-semibold text-gray-700">丢失护照记录</h5>
                <button
                  type="button"
                  onClick={addLostPassport}
                  className="flex items-center px-3 py-1 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  添加丢失护照
                </button>
              </div>

              <div className="space-y-4">
                {(() => {
                  const lostPassports = data.lost_ppt || [];
                  return lostPassports.map((passport, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg border border-red-200 shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormField
                              id={`lost_ppt_${index}_number`}
                              label={`丢失护照号码 ${index + 1}`}
                              value={passport.lost_ppt_number || ''}
                              onChange={(e) => updateLostPassport(index, 'lost_ppt_number', e.target.value)}
                              placeholder="TEST114514"
                              required
                            />
                            <CountrySelect
                              id={`lost_ppt_${index}_country`}
                              label="护照签发国家"
                              value={passport.lost_ppt_country || ''}
                              onChange={(value) => updateLostPassport(index, 'lost_ppt_country', value)}
                              required
                            />
                          </div>
                          <FormField
                            label="丢失说明"
                            type="textarea"
                            value={passport.explain || ''}
                            onChange={(e) => updateLostPassport(index, 'explain', e.target.value)}
                            placeholder="请详细说明护照丢失的情况、时间、地点等..."
                          />
                        </div>
                        {lostPassports.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLostPassport(index)}
                            className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md mt-6"
                            title="删除此丢失护照记录"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}
        </div>

        {/* 预览信息 */}
        {(data.ppt_number || data.ppt_type) && (
          <div className="mt-6 p-4 bg-gradient-to-r from-teal-50 to-green-50 rounded-lg border-l-4 border-teal-400 shadow-sm">
            <h4 className="font-semibold text-teal-800 mb-2">信息预览</h4>
            <div className="text-sm text-teal-700 space-y-1">
              {data.ppt_type && (
                <p><strong>护照类型:</strong> {PASSPORT_TYPES.find(t => t.value === data.ppt_type)?.label || data.ppt_type}</p>
              )}
              {data.ppt_number && <p><strong>护照号码:</strong> {data.ppt_number}</p>}
              {data.ppt_book_number && <p><strong>护照本号码:</strong> {data.ppt_book_number}</p>}
              {data.ppt_issued_country && (
                <p><strong>签发国家:</strong> {getCountryName(data.ppt_issued_country)}</p>
              )}
              {data.ppt_issued_year && data.ppt_issued_month && data.ppt_issued_day && (
                <p><strong>签发日期:</strong> {data.ppt_issued_year}-{data.ppt_issued_month}-{data.ppt_issued_day}</p>
              )}
              <p><strong>是否有到期日期:</strong> {data.has_ppt_expiration ? '✅ 是' : '❌ 否'}</p>
              {data.has_ppt_expiration && data.ppt_expired_year && data.ppt_expired_month && data.ppt_expired_day && (
                <p><strong>到期日期:</strong> {data.ppt_expired_year}-{data.ppt_expired_month}-{data.ppt_expired_day}</p>
              )}
              <p><strong>是否有丢失护照:</strong> {data.ppt_lost ? '⚠️ 是' : '✅ 否'}</p>
              {data.ppt_lost && data.lost_ppt && data.lost_ppt.length > 0 && (
                <p><strong>丢失护照数量:</strong> {data.lost_ppt.length} 个</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PassportVisaForm;
